/**
 * GPX Data Transformers
 * Convert between different GPS data formats (GeoJSON, path arrays, etc.)
 */

interface GpxData {
  path: [number, number][];
  distance: number;
  center?: [number, number];
  timestamps?: (string | Date)[];
  elevationGain?: number;
  startPoint?: [number, number];
  endPoint?: [number, number];
}

/**
 * Parse FeatureCollection from trail data into usable GPX data
 * @param featureCollection GeoJSON FeatureCollection object
 * @returns Parsed GPX data or null if invalid
 */
export const parseFeatureCollection = (
  featureCollection: any,
): GpxData | null => {
  if (
    !featureCollection ||
    featureCollection.type !== "FeatureCollection" ||
    !featureCollection.features?.[0]
  ) {
    return null;
  }

  const feature = featureCollection.features[0];
  if (feature.geometry?.type !== "LineString") return null;

  const path = feature.geometry.coordinates.map(
    ([lon, lat]: [number, number]) => [lat, lon] as [number, number],
  );

  if (path.length === 0) return null;

  const avgLat = path.reduce((sum:any, p:any) => sum + p[0], 0) / path.length;
  const avgLon = path.reduce((sum:any, p:any) => sum + p[1], 0) / path.length;

  return {
    path,
    center: [avgLat, avgLon],
    timestamps: feature.properties?.timestamps,
    distance: 0, // Will be calculated separately using calculateDistance
  };
};

/**
 * Create GeoJSON FeatureCollection from GPX data for storage
 * @param gpxData Parsed GPX data
 * @returns GeoJSON FeatureCollection or undefined if no data
 */
export const createGeoJSONFeatureCollection = (gpxData: GpxData | null) => {
  if (!gpxData) return undefined;

  const coordinates = gpxData.path.map((p: [number, number]) => [p[1], p[0]]);

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: coordinates,
        },
        properties: {
          timestamps: gpxData.timestamps,
        },
      },
    ],
  };
};

/**
 * Convert coordinates to GeoJSON Point
 * @param coords [latitude, longitude]
 * @returns GeoJSON Point object with proper tuple type
 */
export const coordsToGeoJSONPoint = (coords: [number, number]) => {
  return {
    type: "Point" as const,
    coordinates: [coords[1], coords[0]] as [number, number], // [lon, lat] for GeoJSON with tuple type
  };
};
